import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import useEnforce from '../../../../hooks/useEnforce';
import { permissions } from '../../../../util/abac';
import { useCanGenerateWithAi } from './useCanGenerateWithAi';

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(4),
  },
  cards: {
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    maxWidth: theme.pxToRem(880),
    margin: '0 auto',
    width: '100%',
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(12),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  cardTitle: {
    fontWeight: 700,
  },
  heading: {
    textAlign: 'center',
  },
}));

const messages = defineMessages({
  heading: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.heading',
    defaultMessage: 'No action plan yet',
  },
  body: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.body',
    defaultMessage: 'This member does not have an action plan yet.',
  },
  startTitle: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.startTitle',
    defaultMessage: 'Start an action plan',
  },
  startBody: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.startBody',
    defaultMessage:
      'Create a blank Active plan you fill in yourself. The member sees nothing until you add and publish items.',
  },
  startButton: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.startButton',
    defaultMessage: 'Start an action plan',
  },
  generateTitle: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.generateTitle',
    defaultMessage: 'Generate with AI',
  },
  generateBody: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.generateBody',
    defaultMessage:
      'Ask the health-summary agent to draft items. They land hidden from the member until you publish each one.',
  },
  generateButton: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.empty.generateButton',
    defaultMessage: 'Generate with AI',
  },
});

export const ClinicalActionPlanEmptyState: React.FC<{
  onStart: () => void;
  onGenerate: () => void;
  isCreating?: boolean;
  isGenerating?: boolean;
}> = ({ onStart, onGenerate, isCreating, isGenerating }) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const enforcePermission = useEnforce();
  const canCreate = enforcePermission(permissions.createData);
  const { showGenerate } = useCanGenerateWithAi();
  const showStart = canCreate && !isGenerating;

  return (
    <Box
      className={classes.root}
      direction="column"
      align="center"
      justify="center"
      gap={4}
      fullHeight
      fullWidth
    >
      <Box
        className={classes.heading}
        direction="column"
        align="center"
        gap={1}
      >
        <Text role="heading" aria-level={2}>
          {formatMessage(messages.heading)}
        </Text>
        <Text>{formatMessage(messages.body)}</Text>
      </Box>
      {showStart || showGenerate ? (
        <div className={classes.cards}>
          {showStart ? (
            <div className={classes.card}>
              <Text className={classes.cardTitle}>
                {formatMessage(messages.startTitle)}
              </Text>
              <Text>{formatMessage(messages.startBody)}</Text>
              <Button
                variant="outlined"
                onClick={onStart}
                disabled={isCreating}
              >
                {formatMessage(messages.startButton)}
              </Button>
            </div>
          ) : null}
          {showGenerate ? (
            <div className={classes.card}>
              <Text className={classes.cardTitle}>
                {formatMessage(messages.generateTitle)}
              </Text>
              <Text>{formatMessage(messages.generateBody)}</Text>
              <Button
                variant="outlined"
                onClick={onGenerate}
                disabled={isGenerating}
              >
                {formatMessage(messages.generateButton)}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Box>
  );
};
